import React, { useState, type ComponentType } from 'react';
import { X, ChevronLeft, ChevronRight, FileText, Building2, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
interface LegalFiling {
  type: 'lien' | 'ucc' | 'bankruptcy';
  title: string;
  icon: ComponentType<{
    className?: string;
  }>;
  fields: Array<{
    label: string;
    value: string;
    hasStatusDot?: boolean;
    statusColor?: 'green' | 'gray';
  }>;
}
const filings: LegalFiling[] = [{
  type: 'lien',
  title: 'Lien Details',
  icon: FileText,
  fields: [{
    label: 'Jurisdiction:',
    value: 'LACKAWANNA COUNTY RECORDER'
  }, {
    label: 'Filing Type:',
    value: 'STATE TAX LIEN'
  }, {
    label: 'Status:',
    value: 'Released',
    hasStatusDot: true,
    statusColor: 'green'
  }, {
    label: 'Filed Date:',
    value: '4/25/2016'
  }, {
    label: 'Filing Number:',
    value: '201604250892'
  }, {
    label: 'Amount:',
    value: '$1,118'
  }]
}, {
  type: 'ucc',
  title: 'UCC Details',
  icon: Building2,
  fields: [{
    label: 'Jurisdiction:',
    value: 'SEC OF STATE PA'
  }, {
    label: 'Filing Office:',
    value: 'SEC OF STATE PA P O BOX 8721 HARRISBURG PA 17105'
  }, {
    label: 'Status:',
    value: 'Active',
    hasStatusDot: true,
    statusColor: 'green'
  }, {
    label: 'Filed Date:',
    value: '2/14/2023'
  }, {
    label: 'Filing Number:',
    value: '2300014823'
  }, {
    label: 'Secured Party Name:',
    value: 'DE LAGE LANDEN FINANCIAL SERVICES INC'
  }, {
    label: 'Secured Party Address:',
    value: '1111 OLD EAGLE SCHOOL RD, WAYNE, PA'
  }, {
    label: 'Collateral:',
    value: 'OFFICE EQUIPMENT INCLUDING COPIERS AND PRINTERS'
  }]
}, {
  type: 'bankruptcy',
  title: 'Bankruptcy Details',
  icon: AlertCircle,
  fields: [{
    label: 'Status:',
    value: 'Dismissed',
    hasStatusDot: true,
    statusColor: 'gray'
  }, {
    label: 'Filing Type:',
    value: 'CHAPTER 7 FILING (INVOLUNTARY)'
  }, {
    label: 'Filing Number:',
    value: '250831447'
  }, {
    label: 'Filed Date:',
    value: '6/15/2019'
  }, {
    label: 'Released Date:',
    value: '9/22/2019'
  }, {
    label: 'Law Firm:',
    value: 'SWIDLER BERLIN SHEREFF FRIEDMAN LLP'
  }, {
    label: 'Attorney:',
    value: 'JAMES P HALPERT'
  }]
}];
interface LegalFilingsModalProps {
  onClose: () => void;
  initialIndex?: number;
}
export function LegalFilingsModal({
  onClose,
  initialIndex = 0
}: LegalFilingsModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const currentFiling = filings[currentIndex];
  const Icon = currentFiling.icon;
  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  const handleNext = () => {
    if (currentIndex < filings.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1473">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 0.95
    }} className="bg-white rounded-lg shadow-xl w-full max-w-3xl" data-id="element-1474">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between" data-id="element-1475">
          <div className="flex items-center gap-4" data-id="element-1476">
            <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center shrink-0" data-id="element-1477">
              <Icon className="w-7 h-7 text-gray-500" data-id="element-1478" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-1479">
              {currentFiling.title}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1480">
            <X className="w-6 h-6" data-id="element-1481" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6" data-id="element-1482">
          <div className="space-y-0" data-id="element-1483">
            {currentFiling.fields.map((field, index) => <div key={index} className="grid grid-cols-[200px_1fr] gap-8 py-4 border-b border-gray-100 last:border-b-0" data-id="element-1484">
                <div className="font-bold text-gray-900 text-base" data-id="element-1485">
                  {field.label}
                </div>
                <div className="text-gray-900 text-base flex items-center gap-2" data-id="element-1486">
                  {field.hasStatusDot && <div className={`w-2.5 h-2.5 rounded-full ${field.statusColor === 'green' ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-1487" />}
                  {field.value}
                </div>
              </div>)}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 border-t border-gray-200 flex items-center justify-center gap-4" data-id="element-1488">
          <button onClick={handlePrevious} disabled={currentIndex === 0} className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${currentIndex === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`} data-id="element-1489">
            <ChevronLeft className="w-5 h-5" data-id="element-1490" />
            PREVIOUS
          </button>
          <button onClick={handleNext} disabled={currentIndex === filings.length - 1} className={`px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${currentIndex === filings.length - 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`} data-id="element-1491">
            NEXT
            <ChevronRight className="w-5 h-5" data-id="element-1492" />
          </button>
        </div>
      </motion.div>
    </div>;
}